import React from 'react';
import {Badge, Box, Chip, Collapse, IconButton, Stack, Typography} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ENTITY_CARD_OPEN_DETAILS_EVENT} from '../tldraw/customShapes/entityCardConstants';
import WhiteboardChartEntityRow from './WhiteboardChartEntityRow';
import EntityPropertyFilterPanel, {createDefaultEntityFilters} from './EntityPropertyFilterPanel';
import {
    getCompleteFilters,
    isEntityFilterActive,
} from '../utils/evaluateEntityPropertyFilters';
import {isFacetChipSelected, toggleFacetChip} from '../utils/chartFacetChips';
import {resolveChartEntityImageUrl} from '../../../utils/entityAvatarUrl';
import {getEntityTypeLabel, getEntityTypeMeta} from '../../../utils/entityTypeMeta';

const OTHER_TYPE_KEY = '__other__';

function groupEntitiesByType(entities) {
    const groups = new Map();
    for (const entity of entities) {
        // Key by friendly label so aliases like GPE+LOC → "Place" and PERSON+PER → "Person"
        // collapse into a single group. Entities with no type go into "Other".
        const key = entity.entityType
            ? (getEntityTypeLabel(entity.entityType.toUpperCase()) || entity.entityType.toUpperCase())
            : OTHER_TYPE_KEY;
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key).push(entity);
    }
    // Sort groups: ascending by count, ties broken alphabetically, "Other" always last
    return [...groups.entries()].sort(([a, entitiesA], [b, entitiesB]) => {
        if (a === OTHER_TYPE_KEY) return 1;
        if (b === OTHER_TYPE_KEY) return -1;
        const diff = entitiesA.length - entitiesB.length;
        if (diff !== 0) return diff;
        return a.localeCompare(b);
    });
}

function EntityTypeGroup({typeKey, entities, onEntityClick, propertiesByEntityId, entityFilters, onTagClick, entityLabelById}) {
    const [open, setOpen] = React.useState(true);
    const isOther = typeKey === OTHER_TYPE_KEY;
    const label = isOther ? 'Other' : typeKey;
    // Derive display meta from the first entity's raw type so colors stay correct
    const firstType = entities[0]?.entityType || null;
    const meta = isOther ? {color: '#9e9e9e', bg: '#f5f5f5'} : getEntityTypeMeta(firstType);

    return (
        <Box>
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                onClick={() => setOpen((v) => !v)}
                sx={{
                    cursor: 'pointer',
                    py: 0.5,
                    px: 0.5,
                    borderRadius: 1,
                    userSelect: 'none',
                    '&:hover': {bgcolor: 'action.hover'},
                }}
            >
                <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                        color: 'text.secondary',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.15s',
                        fontSize: 16,
                    }}
                />
                <Chip
                    label={label}
                    size="small"
                    sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: meta.bg,
                        color: meta.color,
                        border: `1px solid ${meta.color}33`,
                        cursor: 'pointer',
                    }}
                />
                <Typography variant="caption" color="text.disabled" sx={{fontWeight: 600}}>
                    {entities.length}
                </Typography>
            </Stack>
            <Collapse in={open} unmountOnExit={false}>
                <Stack spacing={1} sx={{pt: 0.5, pl: 1}}>
                    {entities.map((entity) => {
                        // Build property tags for this entity (top 4 most useful values)
                        const rawProps = propertiesByEntityId?.[entity.wikidataId] || [];
                        const seenTokens = new Set();
                        const propertyTags = [];
                        for (const item of rawProps) {
                            // Prefer valueLabel (if ever stored), then resolve valueEntityId to a
                            // known entity label, then fall back to raw value / valueEntityId.
                            const valueEntityId = item.valueEntityId || null;
                            const resolvedLabel = item.valueLabel
                                || (valueEntityId ? (entityLabelById?.[valueEntityId] ?? null) : null)
                                || item.value
                                || valueEntityId
                                || null;
                            const token = item.value || valueEntityId || null;
                            const propertyKey = item.propertyId || item.propertyLabel || null;
                            if (!resolvedLabel || !token || !propertyKey || seenTokens.has(resolvedLabel.toLowerCase())) continue;
                            seenTokens.add(resolvedLabel.toLowerCase());
                            // Check if this tag is already an active filter
                            const activeChip = {kind: 'property', token, propertyKey};
                            const active = isFacetChipSelected(entityFilters, activeChip);
                            propertyTags.push({label: resolvedLabel, token, propertyKey, active});
                            if (propertyTags.length >= 4) break;
                        }
                        return (
                            <WhiteboardChartEntityRow
                                key={entity.wikidataId}
                                entity={entity}
                                entityType={entity.entityType}
                                onClick={() => onEntityClick(entity)}
                                propertyTags={propertyTags}
                                onTagClick={onTagClick}
                            />
                        );
                    })}
                </Stack>
            </Collapse>
        </Box>
    );
}

export default function WhiteboardEntitiesTab({
    chartEntities = [],
    entityFilters = [],
    onEntityFiltersChange,
    propertyOptions = [],
    propertiesByEntityId = {},
    summariesByEntityId = {},
    summariesLoading = false,
    visibleEntityIds = null,
}) {
    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const filtersActive = isEntityFilterActive(entityFilters);

    const chartEntitiesForDisplay = React.useMemo(() => {
        const enriched = chartEntities.map((entity) => {
            const summary = summariesByEntityId[entity.wikidataId];
            return {
                ...entity,
                imageUrl: resolveChartEntityImageUrl({
                    wikidataId: entity.wikidataId,
                    imageUrl: entity.imageUrl,
                }),
                entityType: summary?.entityType || null,
            };
        });

        if (!filtersActive || !visibleEntityIds) return enriched;
        return enriched.filter((entity) => visibleEntityIds.has(entity.wikidataId));
    }, [chartEntities, summariesByEntityId, filtersActive, visibleEntityIds]);

    const handleChartEntityClick = React.useCallback((entity) => {
        if (!entity?.wikidataId) return;
        window.dispatchEvent(new CustomEvent(ENTITY_CARD_OPEN_DETAILS_EVENT, {
            detail: {
                shapeId: entity.shapeId || null,
                wikidataId: entity.wikidataId,
                label: entity.label || entity.wikidataId,
                openedFrom: 'panel',
            },
        }));
    }, []);

    /**
     * Tag chip click from WhiteboardChartEntityRow — add or remove the property
     * value as a filter shortcut, same toggle logic as ChartFacetChipBar.
     */
    const handleTagClick = React.useCallback((token, propertyKey) => {
        if (!token || !propertyKey) return;
        const chip = {kind: 'property', token, propertyKey};
        onEntityFiltersChange?.(toggleFacetChip(entityFilters, chip, {
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            summariesLoading,
        }));
    }, [entityFilters, onEntityFiltersChange, chartEntities, propertiesByEntityId, summariesByEntityId, summariesLoading]);

    const handleToggleFilters = React.useCallback(() => {
        const nextOpen = !filtersOpen;
        setFiltersOpen(nextOpen);
        if (nextOpen && entityFilters.length === 0) {
            onEntityFiltersChange?.(createDefaultEntityFilters());
        }
    }, [filtersOpen, entityFilters.length, onEntityFiltersChange]);

    const hasChartEntities = chartEntities.length > 0;
    const completeFilterCount = getCompleteFilters(entityFilters).length;

    // Quick lookup: wikidataId → human label, used to resolve raw QID property values
    const entityLabelById = React.useMemo(() => {
        const map = {};
        for (const entity of chartEntities) {
            if (entity.wikidataId && entity.label) {
                map[entity.wikidataId] = entity.label;
            }
        }
        return map;
    }, [chartEntities]);

    const entityGroups = React.useMemo(
        () => groupEntitiesByType(chartEntitiesForDisplay),
        [chartEntitiesForDisplay],
    );

    return (
        <Stack height="100%" minHeight={0} overflow="hidden">
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{px: 2, pt: 1.5, pb: filtersOpen ? 0.5 : 0}}
            >
                <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{fontWeight: 700, letterSpacing: 0.8, px: 0.5}}
                >
                    ON THIS CHART
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    <IconButton
                        size="small"
                        aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
                        aria-expanded={filtersOpen}
                        color={filtersOpen || filtersActive ? 'primary' : 'default'}
                        onClick={handleToggleFilters}
                    >
                        <Badge
                            color="primary"
                            badgeContent={filtersActive ? completeFilterCount : 0}
                            invisible={!filtersActive}
                        >
                            <FilterListIcon fontSize="small"/>
                        </Badge>
                    </IconButton>
                </Stack>
            </Stack>

            {filtersOpen && (
                <EntityPropertyFilterPanel
                    filters={entityFilters}
                    onFiltersChange={onEntityFiltersChange}
                    propertyOptions={propertyOptions}
                    chartEntities={chartEntities}
                    propertiesByEntityId={propertiesByEntityId}
                    summariesByEntityId={summariesByEntityId}
                    summariesLoading={summariesLoading}
                />
            )}

            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5}}>
                {chartEntitiesForDisplay.length > 0 ? (
                    <Stack spacing={1.5}>
                        {entityGroups.map(([typeKey, groupEntities]) => (
                            <EntityTypeGroup
                                key={typeKey}
                                typeKey={typeKey}
                                entities={groupEntities}
                                onEntityClick={handleChartEntityClick}
                                propertiesByEntityId={propertiesByEntityId}
                                entityFilters={entityFilters}
                                onTagClick={handleTagClick}
                                entityLabelById={entityLabelById}
                            />
                        ))}
                    </Stack>
                ) : hasChartEntities && filtersActive ? (
                    <Typography variant="body2" color="text.secondary">
                        No entities match the current filters.
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No entities on this chart yet. Use the Search tab to add entities.
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}
